import { useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
} from "@tabipla/ui";
import { useAddFriend } from "@/application/hooks/useFriends";

// Lazy load QRScannerDialog to reduce initial bundle size
const QRScannerDialog = lazy(() =>
  import("./QRScannerDialog").then((m) => ({ default: m.QRScannerDialog }))
);

interface AddFriendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddFriendDialog({ open, onOpenChange }: AddFriendDialogProps) {
  const { t } = useTranslation();
  const [friendCode, setFriendCode] = useState("");
  const [showScanner, setShowScanner] = useState(false);

  const addFriend = useAddFriend({
    onSuccess: () => {
      toast.success(t("friends.add.success", "友達を追加しました"));
      setFriendCode("");
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!friendCode.trim()) return;
    addFriend.mutate(friendCode.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t("friends.add.title", "友達を追加")}
          </DialogTitle>
          <DialogDescription>
            {t("friends.add.description", "友達のフレンドコードを入力してください")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={friendCode}
              onChange={(e) => setFriendCode(e.target.value.toUpperCase())}
              placeholder={t("friends.add.placeholder", "フレンドコード")}
              className="flex-1 font-mono tracking-wider"
              maxLength={8}
              disabled={addFriend.isPending}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowScanner(true)}
              disabled={addFriend.isPending}
              title={t("friends.add.scanQR", "QRスキャン")}
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addFriend.isPending}
            >
              {t("common.cancel", "キャンセル")}
            </Button>
            <Button type="submit" disabled={!friendCode.trim() || addFriend.isPending}>
              {addFriend.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.loading", "読み込み中...")}
                </>
              ) : (
                t("friends.add.submit", "追加")
              )}
            </Button>
          </div>
        </form>
      </DialogContent>

      {showScanner && (
        <Suspense fallback={null}>
          <QRScannerDialog
            open={showScanner}
            onOpenChange={setShowScanner}
            onScanSuccess={(code) => {
              setFriendCode(code);
              toast.success(t("friends.scan.success", "コードを読み取りました"));
            }}
          />
        </Suspense>
      )}
    </Dialog>
  );
}
